// web-admin/src/pages/onboarding/OnboardingDashboard.js
import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Card,
  CardContent,
  Stack,
  Fade,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  FormControlLabel,
  Switch,
  InputAdornment,
  Divider,
  Avatar,
  Tooltip
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
  Assignment as ListIcon,
  CheckCircle as ApproveIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  ContactPhone as EmergencyIcon,
  AccountBalance as BankIcon,
  Settings as SettingsIcon
} from "@mui/icons-material";

import AppHeader from "../../components/AppHeader";
import { buildHierarchicalList } from "../../components/staff/constants";

// Helper to strip "/api" from API_URL1 to get host domain
const getBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL1 || "";
  return apiUrl.replace(/\/api$/, "");
};

export default function OnboardingDashboard({ setIsLoggedIn }) {
  const [tabIndex, setTabIndex] = useState(0);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
  const headers = useMemo(() => ({ "X-Tenant": tenant }), [tenant]);

  // Invites State
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ full_name: "", email: "" });
  const [inviting, setInviting] = useState(false);

  // Review Drawer / Dialog State
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [approving, setApproving] = useState(false);

  // HR Assign Fields for Approval
  const [hrAssign, setHrAssign] = useState({
    salutation: "U",
    staff_code: "",
    joining_date: new Date().toISOString().split("T")[0],
    branch_name: "",
    department: "",
    grade_level: "",
    job_title: "",
    basic_salary: 0,
    system_role: "staff"
  });

  // Lookup data for HR assignments
  const [lookups, setLookups] = useState({
    branches: [],
    orgNodes: [],
    grades: [],
    jobRoles: []
  });

  // Settings State
  const [settings, setSettings] = useState({
    smtp_host: "",
    smtp_port: 587,
    smtp_secure: false,
    smtp_user: "",
    smtp_pass: "",
    sender_name: "",
    sender_email: "",
    enable_ai_onboarding_parser: true,
    ai_parse_monthly_limit: 50
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Connection Testing State
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testRecipientEmail, setTestRecipientEmail] = useState("");
  const [testingConnection, setTestingConnection] = useState(false);

  // UI Alerts
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const showSnack = (message, severity = "success") => {
    setSnack({ open: true, message, severity });
  };

  // Load submissions & lookups
  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL1}/onboarding/submissions`, {
        withCredentials: true,
        headers
      });
      if (res.data?.success) {
        setCandidates(res.data.data || []);
      }
    } catch (err) {
      console.error("Load submissions error:", err);
      showSnack("Failed to fetch candidate submissions", "error");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const loadLookups = useCallback(async () => {
    try {
      const endpoints = [
        ["branches", `${process.env.REACT_APP_API_URL1}/som/branches`],
        ["orgNodes", `${process.env.REACT_APP_API_URL1}/som/nodes`],
        ["grades", `${process.env.REACT_APP_API_URL1}/som/grades`],
        ["jobRoles", `${process.env.REACT_APP_API_URL1}/som/roles`]
      ];

      const results = await Promise.all(
        endpoints.map(e =>
          axios
            .get(e[1], { withCredentials: true, headers })
            .then(res => res.data?.data || res.data || [])
            .catch(err => {
              console.warn(`Lookup lookup failed for ${e[0]}:`, err.message);
              return [];
            })
        )
      );

      setLookups({
        branches: results[0] || [],
        orgNodes: buildHierarchicalList(results[1] || []),
        grades: results[2] || [],
        jobRoles: results[3] || []
      });
    } catch (err) {
      console.error("Failed to load lookups:", err);
    }
  }, [headers]);

  // Load Settings
  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL1}/onboarding/settings/smtp`, {
        withCredentials: true,
        headers
      });
      if (res.data?.success) {
        const d = res.data.data || {};
        setSettings({
          smtp_host: d.smtp_host || "",
          smtp_port: d.smtp_port || 587,
          smtp_secure: d.smtp_secure === 1,
          smtp_user: d.smtp_user || "",
          smtp_pass: "", // Do not render password back
          sender_name: d.sender_name || "",
          sender_email: d.sender_email || "",
          enable_ai_onboarding_parser: res.data.enableAiParser !== false,
          ai_parse_monthly_limit: res.data.aiParseMonthlyLimit || 50
        });
      }
    } catch (err) {
      console.error("Load settings error:", err);
      showSnack("Failed to load SMTP/AI Settings", "error");
    } finally {
      setSettingsLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    loadSubmissions();
    loadLookups();
  }, [loadSubmissions, loadLookups]);

  useEffect(() => {
    if (tabIndex === 2) {
      loadSettings();
    }
  }, [tabIndex, loadSettings]);

  // Send Invitation Link
  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    if (!inviteForm.full_name || !inviteForm.email) {
      showSnack("Full name and email are required", "warning");
      return;
    }

    setInviting(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL1}/onboarding/invite`,
        inviteForm,
        { withCredentials: true, headers }
      );
      if (res.data?.success) {
        showSnack(`Invitation email successfully sent to ${inviteForm.email}!`);
        setInviteOpen(false);
        setInviteForm({ full_name: "", email: "" });
        loadSubmissions();
      }
    } catch (err) {
      console.error("Invitation failed:", err);
      showSnack(err.response?.data?.message || "Failed to invite candidate.", "error");
    } finally {
      setInviting(false);
    }
  };

  // Copy tokenized link to clipboard
  const handleCopyLink = (secureToken) => {
    const link = `${window.location.origin}/onboard?token=${secureToken}&tenant=${tenant}`;
    navigator.clipboard.writeText(link);
    showSnack("Onboarding portal link copied to clipboard!");
  };

  // Open candidate details review drawer
  const handleOpenReview = (cand) => {
    setSelectedCandidate(cand);
    
    // Auto-prefill staff code with a clean format if blank
    const randomSuffix = Math.floor(1000 + Math.random() * 9000);
    setHrAssign({
      salutation: "U",
      staff_code: `ST-${randomSuffix}`,
      joining_date: new Date().toISOString().split("T")[0],
      branch_name: lookups.branches[0]?.branch_name || "",
      department: lookups.orgNodes[0]?.node_name || "",
      grade_level: lookups.grades[0]?.grade_name || "",
      job_title: lookups.jobRoles[0]?.role_name || "",
      basic_salary: 0,
      system_role: "staff"
    });
    setReviewOpen(true);
  };

  // Approve and activate employee
  const handleApproveSubmit = async () => {
    if (!hrAssign.staff_code) {
      showSnack("Employee code is required.", "warning");
      return;
    }
    if (!hrAssign.branch_name || !hrAssign.department) {
      showSnack("Branch and Department alignments are required.", "warning");
      return;
    }

    setApproving(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL1}/onboarding/submissions/${selectedCandidate.id}/approve`,
        hrAssign,
        { withCredentials: true, headers }
      );
      if (res.data?.success) {
        showSnack(`Successfully approved employee profile! User account activated.`);
        setReviewOpen(false);
        setSelectedCandidate(null);
        loadSubmissions();
      }
    } catch (err) {
      console.error("Approval failed:", err);
      showSnack(err.response?.data?.message || "Failed to approve employee profile.", "error");
    } finally {
      setApproving(false);
    }
  };

  // Save Settings Changes
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSettingsSaving(true);
    try {
      const payload = {
        smtp_host: settings.smtp_host,
        smtp_port: parseInt(settings.smtp_port, 10),
        smtp_secure: settings.smtp_secure,
        smtp_user: settings.smtp_user,
        sender_name: settings.sender_name,
        sender_email: settings.sender_email,
        enable_ai_onboarding_parser: settings.enable_ai_onboarding_parser,
        ai_parse_monthly_limit: parseInt(settings.ai_parse_monthly_limit, 10)
      };

      if (settings.smtp_pass) {
        payload.smtp_pass = settings.smtp_pass;
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL1}/onboarding/settings/smtp`,
        payload,
        { withCredentials: true, headers }
      );

      if (res.data?.success) {
        showSnack("Onboarding and SMTP settings saved successfully!");
        setSettings(prev => ({ ...prev, smtp_pass: "" })); // Clear local password state
      }
    } catch (err) {
      console.error("Save settings failed:", err);
      showSnack(err.response?.data?.message || "Failed to update settings.", "error");
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleOpenTestEmail = () => {
    setTestRecipientEmail(settings.sender_email || "");
    setTestEmailOpen(true);
  };

  const handleTestConnection = async (e) => {
    if (e) e.preventDefault();
    if (!settings.smtp_host || !settings.smtp_port || !settings.smtp_user || !settings.sender_name || !settings.sender_email) {
      showSnack("Please fill in SMTP Host, Port, Username, Sender Name, and Sender Email first.", "warning");
      return;
    }
    if (!testRecipientEmail) {
      showSnack("Please provide a recipient email address for the test.", "warning");
      return;
    }

    setTestingConnection(true);
    try {
      const payload = {
        smtp_host: settings.smtp_host,
        smtp_port: parseInt(settings.smtp_port, 10),
        smtp_secure: settings.smtp_secure,
        smtp_user: settings.smtp_user,
        sender_name: settings.sender_name,
        sender_email: settings.sender_email,
        test_recipient: testRecipientEmail
      };

      if (settings.smtp_pass) {
        payload.smtp_pass = settings.smtp_pass;
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL1}/onboarding/settings/smtp/test`,
        payload,
        { withCredentials: true, headers }
      );

      if (res.data?.success) {
        showSnack(res.data.message || `Test email successfully sent to ${testRecipientEmail}!`);
        setTestEmailOpen(false);
      }
    } catch (err) {
      console.error("Test connection failed:", err);
      showSnack(err.response?.data?.message || "SMTP connection test failed.", "error");
    } finally {
      setTestingConnection(false);
    }
  };

  // Render Status Badge
  const getStatusChip = (status) => {
    const config = {
      INVITED: { label: "Invited", color: "info" },
      IN_PROGRESS: { label: "In Progress", color: "warning" },
      SUBMITTED: { label: "Submitted", color: "success" }
    }[status] || { label: status, color: "default" };

    return <Chip label={config.label} color={config.color} size="small" sx={{ fontWeight: 700 }} />;
  };

  // Helper to parse submitted candidate data
  const candidateData = useMemo(() => {
    if (!selectedCandidate?.submitted_data) return {};
    const d = selectedCandidate.submitted_data;
    return typeof d === "string" ? JSON.parse(d) : d;
  }, [selectedCandidate]);

  // Filtered submissions for search
  // Filtered submissions for search (Tab 0: Invited/In Progress)
  const invitedCandidates = useMemo(() => {
    return candidates.filter(
      c =>
        (c.status === "INVITED" || c.status === "IN_PROGRESS") &&
        (c.full_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          c.email?.toLowerCase().includes(searchText.toLowerCase()))
    );
  }, [candidates, searchText]);

  // Filtered submissions for search (Tab 1: Submitted profiles waiting review)
  const submittedCandidates = useMemo(() => {
    return candidates.filter(
      c =>
        c.status === "SUBMITTED" &&
        (c.full_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          c.email?.toLowerCase().includes(searchText.toLowerCase()))
    );
  }, [candidates, searchText]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", display: "flex", flexDirection: "column" }}>
      <AppHeader setIsLoggedIn={setIsLoggedIn} />

      <Box sx={{ p: 4, flexGrow: 1, maxWidth: 1400, mx: "auto", width: "100%" }}>
        <Fade in timeout={500}>
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ p: 1, bgcolor: "#eef2ff", borderRadius: 2.5, display: "flex" }}>
                <PersonAddIcon sx={{ color: "primary.main", fontSize: 24 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
                Onboarding Pilot Centre
              </Typography>
            </Stack>
            <Typography variant="body2" color="#64748b" sx={{ ml: 6 }}>
              Invite new hires, configure dynamic SMTP configs, and review AI-prefilled candidate documents.
            </Typography>
          </Box>
        </Fade>

        {/* Dashboard Tabs */}
        <Paper sx={{ borderRadius: 4, mb: 4, overflow: "hidden", boxShadow: "0 4px 25px 0 rgba(0,0,0,0.03)" }}>
          <Tabs
            value={tabIndex}
            onChange={(e, val) => setTabIndex(val)}
            sx={{
              bgcolor: "#fff",
              borderBottom: "1px solid #f1f5f9",
              "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0", bgcolor: "primary.main" },
              "& .MuiTab-root": { py: 2.5, fontWeight: 700, color: "#64748b", textTransform: "none", fontSize: 14 },
              "& .Mui-selected": { color: "#6366f1 !important" }
            }}
          >
            <Tab icon={<ListIcon fontSize="small" />} iconPosition="start" label="Candidates & Invites" />
            <Tab icon={<ApproveIcon fontSize="small" />} iconPosition="start" label="Pending Approvals" />
            <Tab icon={<SettingsIcon fontSize="small" />} iconPosition="start" label="SMTP & Settings" />
          </Tabs>

          {/* TAB 0: Invited Candidates */}
          {tabIndex === 0 && (
            <Box sx={{ p: 4 }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
                <TextField
                  placeholder="Search candidates..."
                  size="small"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2.5, bgcolor: "#f8fafc", "& fieldset": { border: "none" } }
                  }}
                  sx={{ width: 300 }}
                />

                <Stack direction="row" spacing={2}>
                  <Tooltip title="Refresh List">
                    <IconButton onClick={loadSubmissions} sx={{ bgcolor: "#f1f5f9", borderRadius: 2.5 }}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setInviteOpen(true)}
                    sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" }, borderRadius: 2.5, px: 3, fontWeight: 700, textTransform: "none" }}
                  >
                    Invite New Hire
                  </Button>
                </Stack>
              </Stack>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress color="primary" />
                </Box>
              ) : invitedCandidates.length === 0 ? (
                <Box sx={{ py: 8, textAlign: "center" }}>
                  <Typography color="text.secondary">No invited or active candidate portals found.</Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #f1f5f9", borderRadius: 3 }}>
                  <Table>
                    <TableHead sx={{ bgcolor: "#f8fafc" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Full Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Email Address</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Onboarding Link</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Date Invited</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invitedCandidates.map(c => (
                        <TableRow key={c.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 700, color: "#1e293b" }}>{c.full_name}</TableCell>
                          <TableCell color="text.secondary">{c.email}</TableCell>
                          <TableCell>
                            {c.status !== "APPROVED" && (
                              <Tooltip title="Copy Portal Invite Link">
                                <IconButton size="small" onClick={() => handleCopyLink(c.secure_token)} sx={{ color: "primary.main" }}>
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>{getStatusChip(c.status)}</TableCell>
                          <TableCell color="text.secondary">
                            {new Date(c.created_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* TAB 1: Pending Approvals */}
          {tabIndex === 1 && (
            <Box sx={{ p: 4 }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
                <TextField
                  placeholder="Search submissions..."
                  size="small"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2.5, bgcolor: "#f8fafc", "& fieldset": { border: "none" } }
                  }}
                  sx={{ width: 300 }}
                />

                <Tooltip title="Refresh Submissions">
                  <IconButton onClick={loadSubmissions} sx={{ bgcolor: "#f1f5f9", borderRadius: 2.5 }}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress color="primary" />
                </Box>
              ) : submittedCandidates.length === 0 ? (
                <Box sx={{ py: 8, textAlign: "center" }}>
                  <Typography color="text.secondary">No submitted profiles pending approval.</Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #f1f5f9", borderRadius: 3 }}>
                  <Table>
                    <TableHead sx={{ bgcolor: "#f8fafc" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Full Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Email Address</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Date Submitted</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#475569", textAlign: "right" }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {submittedCandidates.map(c => (
                        <TableRow key={c.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 700, color: "#1e293b" }}>{c.full_name}</TableCell>
                          <TableCell color="text.secondary">{c.email}</TableCell>
                          <TableCell>{getStatusChip(c.status)}</TableCell>
                          <TableCell color="text.secondary">
                            {new Date(c.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell sx={{ textAlign: "right" }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<ApproveIcon />}
                              onClick={() => handleOpenReview(c)}
                              sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none", py: 0.6 }}
                            >
                              Review & Approve
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* TAB 2: SMTP Settings */}
          {tabIndex === 2 && (
            <Box sx={{ p: 4, maxWidth: 800 }}>
              {settingsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <form onSubmit={handleSaveSettings}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", mb: 2 }}>
                    AI Parser Toggle & Costs Quota
                  </Typography>

                  <Stack spacing={3} sx={{ mb: 4 }}>
                    <Card variant="outlined" sx={{ borderRadius: 3, border: "1px solid #f1f5f9" }}>
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.enable_ai_onboarding_parser}
                              onChange={e =>
                                setSettings(prev => ({ ...prev, enable_ai_onboarding_parser: e.target.checked }))
                              }
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Enable Gemini AI Parsing
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Parse resumes (CV) and NRC Card details automatically. If disabled, candidate enters details manually directly.
                              </Typography>
                            </Box>
                          }
                        />

                        <Box sx={{ mt: 3, maxWidth: 300 }}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Monthly Parsing Invite Limit"
                            value={settings.ai_parse_monthly_limit}
                            onChange={e =>
                              setSettings(prev => ({ ...prev, ai_parse_monthly_limit: e.target.value }))
                            }
                            InputLabelProps={{ shrink: true }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                          />
                        </Box>
                      </CardContent>
                    </Card>

                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", mt: 2 }}>
                      Tenant SMTP Email Credentials
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                      Configure custom mail account settings. If left blank, the portal will fallback to the system default host parameters.
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          label="SMTP Host"
                          placeholder="smtp.mailgun.org or smtp.gmail.com"
                          value={settings.smtp_host}
                          onChange={e => setSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="SMTP Port"
                          placeholder="587"
                          value={settings.smtp_port}
                          onChange={e => setSettings(prev => ({ ...prev, smtp_port: e.target.value }))}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="SMTP User / Username"
                          value={settings.smtp_user}
                          onChange={e => setSettings(prev => ({ ...prev, smtp_user: e.target.value }))}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="password"
                          label="SMTP Password"
                          placeholder={settings.smtp_host ? "••••••••" : "Enter Password"}
                          value={settings.smtp_pass}
                          onChange={e => setSettings(prev => ({ ...prev, smtp_pass: e.target.value }))}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Sender Sender Name"
                          placeholder="e.g. HR Team"
                          value={settings.sender_name}
                          onChange={e => setSettings(prev => ({ ...prev, sender_name: e.target.value }))}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Sender Email Address"
                          placeholder="hr@mycompany.com"
                          value={settings.sender_email}
                          onChange={e => setSettings(prev => ({ ...prev, sender_email: e.target.value }))}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.smtp_secure}
                              onChange={e => setSettings(prev => ({ ...prev, smtp_secure: e.target.checked }))}
                            />
                          }
                          label="Use Secure SSL/TLS Connections (usually port 465)"
                        />
                      </Grid>
                    </Grid>

                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={settingsSaving}
                        sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" }, borderRadius: 2.5, px: 4, py: 1.2, fontWeight: 700, textTransform: "none" }}
                      >
                        {settingsSaving ? "Saving..." : "Save Config Settings"}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleOpenTestEmail}
                        disabled={settingsSaving}
                        sx={{ borderColor: "primary.main", color: "primary.main", "&:hover": { borderColor: "primary.dark", bgcolor: (theme) => `${theme.palette.primary.main}0a` }, borderRadius: 2.5, px: 4, py: 1.2, fontWeight: 700, textTransform: "none" }}
                      >
                        Test SMTP Connection
                      </Button>
                    </Stack>
                  </Stack>
                </form>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      {/* MODAL DIALOG: Invite candidate */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, borderBottom: "1px solid #f1f5f9" }}>Invite New Candidate</DialogTitle>
        <form onSubmit={handleInviteSubmit}>
          <DialogContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Candidate Name"
                placeholder="John Doe"
                value={inviteForm.full_name}
                onChange={e => setInviteForm(prev => ({ ...prev, full_name: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
              />
              <TextField
                fullWidth
                type="email"
                label="Candidate Email"
                placeholder="johndoe@example.com"
                value={inviteForm.email}
                onChange={e => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: "1px solid #f1f5f9" }}>
            <Button onClick={() => setInviteOpen(false)} sx={{ color: "#64748b" }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={inviting}
              sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" }, borderRadius: 2.5, px: 3, fontWeight: 700 }}
            >
              {inviting ? "Sending invite..." : "Send Invitation link"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* DRAWER/DIALOG: Review Submitted Onboarding Profile */}
      <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="lg" fullWidth scroll="body">
        <DialogTitle sx={{ fontWeight: 900, borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Review Candidate Submission</span>
          {selectedCandidate && (
            <Chip label={selectedCandidate.email} color="primary" variant="outlined" size="small" />
          )}
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          {selectedCandidate && (
            <Grid container spacing={4}>
              {/* Left Column: Candidate Submitted Profile Details */}
              <Grid item xs={12} md={7}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, color: "#1e293b" }}>
                  Submitted Profile Details
                </Typography>

                <Stack spacing={3}>
                  {/* Photo Preview & Basic Title info */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 3, p: 2.5, bgcolor: "#f8fafc", borderRadius: 4, border: "1px solid #f1f5f9" }}>
                    <Avatar
                      src={
                        selectedCandidate.profile_photo_url
                          ? `${getBaseUrl()}${selectedCandidate.profile_photo_url}`
                          : "/default-avatar.png"
                      }
                      sx={{ width: 90, height: 90, border: "3px solid #6366f1", boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}26` }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                        {selectedCandidate.full_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Submitted: {new Date(selectedCandidate.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Core personal data */}
                  <Card variant="outlined" sx={{ borderRadius: 3, border: "1px solid #f1f5f9" }}>
                    <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <DetailRow label="Salutation Prefilled" value={candidateData.salutation || "None"} />
                      <DetailRow label="Gender" value={candidateData.gender || "MALE"} />
                      <DetailRow label="Date of Birth" value={candidateData.dob || "-"} />
                      <DetailRow label="NRC / ID Number" value={candidateData.nrc_number || "-"} highlight />
                      <DetailRow label="Phone Number" value={candidateData.phone || "-"} />
                      <DetailRow label="Marital Status" value={candidateData.marital_status || "-"} />
                      <DetailRow label="Blood Type" value={candidateData.blood_group || "-"} />
                      <DetailRow label="Passport Number" value={candidateData.passport_number || "-"} />
                      <DetailRow label="Social Security No (SSB)" value={candidateData.social_security_no || "-"} />
                      <DetailRow label="Legal Current Address" value={candidateData.address || "-"} />
                    </CardContent>
                  </Card>

                  {/* Emergency and Bank Info */}
                  <Card variant="outlined" sx={{ borderRadius: 3, border: "1px solid #f1f5f9" }}>
                    <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "primary.main", mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                        <BankIcon fontSize="small" /> Bank Details
                      </Typography>
                      <DetailRow label="Bank Name" value={candidateData.bank_name || "-"} />
                      <DetailRow label="Bank Account Number" value={candidateData.bank_account || "-"} highlight />

                      <Divider sx={{ my: 1 }} />

                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "primary.main", mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                        <EmergencyIcon fontSize="small" /> Emergency Contact
                      </Typography>
                      <DetailRow label="Contact Person" value={candidateData.emergency_contact_name || "-"} />
                      <DetailRow label="Relationship" value={candidateData.emergency_contact_relation || "-"} />
                      <DetailRow label="Contact Phone" value={candidateData.emergency_contact_phone || "-"} />
                    </CardContent>
                  </Card>

                  {/* Attachments Section */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                    Uploaded Document Files
                  </Typography>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                    {selectedCandidate.cv_url && (
                      <Button
                        variant="outlined"
                        href={`${getBaseUrl()}${selectedCandidate.cv_url}`}
                        target="_blank"
                        size="small"
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        Open CV PDF
                      </Button>
                    )}
                    {selectedCandidate.nrc_front_url && (
                      <Button
                        variant="outlined"
                        href={`${getBaseUrl()}${selectedCandidate.nrc_front_url}`}
                        target="_blank"
                        size="small"
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        NRC Front Image
                      </Button>
                    )}
                    {selectedCandidate.nrc_back_url && (
                      <Button
                        variant="outlined"
                        href={`${getBaseUrl()}${selectedCandidate.nrc_back_url}`}
                        target="_blank"
                        size="small"
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        NRC Back Image
                      </Button>
                    )}
                    {!selectedCandidate.nrc_front_url && selectedCandidate.id_document_url && (
                      <Button
                        variant="outlined"
                        href={`${getBaseUrl()}${selectedCandidate.id_document_url}`}
                        target="_blank"
                        size="small"
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        Open NRC Scan ID
                      </Button>
                    )}
                    {candidateData.cv_file_url && !selectedCandidate.cv_url && (
                      <Button
                        variant="outlined"
                        href={`${getBaseUrl()}${candidateData.cv_file_url}`}
                        target="_blank"
                        size="small"
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        Open CV / Resume PDF
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Grid>

              {/* Right Column: HR Assignment Config Fields */}
              <Grid item xs={12} md={5}>
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 4, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, color: "#0f172a" }}>
                    HR Staff Alignment
                  </Typography>

                  <Stack spacing={2.5}>
                    <TextField
                      fullWidth
                      label="Employee ID / Staff Code"
                      value={hrAssign.staff_code}
                      onChange={e => setHrAssign(prev => ({ ...prev, staff_code: e.target.value }))}
                      sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                    />

                    <FormControl fullWidth sx={{ bgcolor: "#fff" }}>
                      <InputLabel>Salutation</InputLabel>
                      <Select
                        value={hrAssign.salutation}
                        onChange={e => setHrAssign(prev => ({ ...prev, salutation: e.target.value }))}
                        sx={{ borderRadius: 2.5 }}
                      >
                        <MenuItem value="U">U</MenuItem>
                        <MenuItem value="Daw">Daw</MenuItem>
                        <MenuItem value="ဦး">ဦး</MenuItem>
                        <MenuItem value="ဒေါ်">ဒေါ်</MenuItem>
                        <MenuItem value="Mr.">Mr.</MenuItem>
                        <MenuItem value="Mrs.">Mrs.</MenuItem>
                        <MenuItem value="Ms.">Ms.</MenuItem>
                        <MenuItem value="Dr.">Dr.</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      type="date"
                      label="Employment Joining Date"
                      value={hrAssign.joining_date}
                      onChange={e => setHrAssign(prev => ({ ...prev, joining_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                    />

                    <FormControl fullWidth sx={{ bgcolor: "#fff" }}>
                      <InputLabel>Official Branch</InputLabel>
                      <Select
                        value={hrAssign.branch_name}
                        onChange={e => setHrAssign(prev => ({ ...prev, branch_name: e.target.value }))}
                        sx={{ borderRadius: 2.5 }}
                      >
                        {lookups.branches.map(b => (
                          <MenuItem key={b.id} value={b.branch_name}>
                            {b.branch_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ bgcolor: "#fff" }}>
                      <InputLabel>Department / Org Unit</InputLabel>
                      <Select
                        value={hrAssign.department}
                        onChange={e => setHrAssign(prev => ({ ...prev, department: e.target.value }))}
                        sx={{ borderRadius: 2.5 }}
                      >
                        {lookups.orgNodes.map((n) => (
                          <MenuItem key={n.id} value={n.node_name} sx={{ pl: 2 + (n._depth || 0) * 2 }}>
                            {n._depth > 0 && <span style={{ color: '#94a3b8', marginRight: 4 }}>{'└'}</span>}
                            {n.node_name}{n.level_type ? ` (${n.level_type})` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ bgcolor: "#fff" }}>
                      <InputLabel>Grade Level</InputLabel>
                      <Select
                        value={hrAssign.grade_level}
                        onChange={e => setHrAssign(prev => ({ ...prev, grade_level: e.target.value }))}
                        sx={{ borderRadius: 2.5 }}
                      >
                        {lookups.grades.map(g => (
                          <MenuItem key={g.id} value={g.grade_name}>
                            {g.grade_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ bgcolor: "#fff" }}>
                      <InputLabel>Job Title / Role</InputLabel>
                      <Select
                        value={hrAssign.job_title}
                        onChange={e => setHrAssign(prev => ({ ...prev, job_title: e.target.value }))}
                        sx={{ borderRadius: 2.5 }}
                      >
                        {lookups.jobRoles.map(j => (
                          <MenuItem key={j.id} value={j.role_name}>
                            {j.role_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      type="number"
                      label="Basic Salary (Monthly)"
                      value={hrAssign.basic_salary}
                      onChange={e => setHrAssign(prev => ({ ...prev, basic_salary: parseFloat(e.target.value || 0) }))}
                      sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                    />

                    <FormControl fullWidth sx={{ bgcolor: "#fff" }}>
                      <InputLabel>System Security Role</InputLabel>
                      <Select
                        value={hrAssign.system_role}
                        onChange={e => setHrAssign(prev => ({ ...prev, system_role: e.target.value }))}
                        sx={{ borderRadius: 2.5 }}
                      >
                        <MenuItem value="staff">Staff</MenuItem>
                        <MenuItem value="line_manager">Line Manager</MenuItem>
                        <MenuItem value="hr_manager">HR Manager</MenuItem>
                        <MenuItem value="support_admin">Support Admin</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 4, borderTop: "1px solid #f1f5f9" }}>
          <Button onClick={() => setReviewOpen(false)} sx={{ color: "#64748b" }}>
            Close
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<ApproveIcon />}
            disabled={approving}
            onClick={handleApproveSubmit}
            sx={{ borderRadius: 2.5, px: 4, py: 1, fontWeight: 700 }}
          >
            {approving ? "Approving..." : "Approve & Activate Employee"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DIALOG: Test SMTP Connection */}
      <Dialog open={testEmailOpen} onClose={() => setTestEmailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, borderBottom: "1px solid #f1f5f9" }}>Test SMTP Connection</DialogTitle>
        <form onSubmit={handleTestConnection}>
          <DialogContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                Enter a recipient email address to send a test email. This verifies if the host settings, authentication details, and connection port are correct.
              </Typography>
              <TextField
                fullWidth
                type="email"
                label="Test Recipient Email"
                placeholder="recipient@example.com"
                value={testRecipientEmail}
                onChange={e => setTestRecipientEmail(e.target.value)}
                required
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: "1px solid #f1f5f9" }}>
            <Button onClick={() => setTestEmailOpen(false)} sx={{ color: "#64748b", textTransform: "none", fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={testingConnection}
              sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" }, borderRadius: 2.5, px: 3, fontWeight: 700, textTransform: "none" }}
            >
              {testingConnection ? "Testing Connection..." : "Send Test Email"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Alerts Toast */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Simple DetailRow component
const DetailRow = ({ label, value, highlight }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 700,
        color: highlight ? "primary.main" : "#1e293b",
        fontSize: "0.875rem"
      }}
    >
      {value}
    </Typography>
  </Stack>
);
